import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Chip,
  Switch,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { logout } from "../api/auth";
import {
  fetchAccountSettings,
  fetchSupportProfile,
  fetchTrustBudPreferences,
  changeAccountPassword,
  deleteAccount,
  updateTrustBudPreferences,
  updateSupportProfile,
  updateProfileImage,
} from "../api/user";
import { useEntitlements } from "../billing/useEntitlements";

function normalizeTrustBudPreferences(prefs = {}) {
  return {
    is_available_for_trustbud: Boolean(prefs?.is_available_for_trustbud),
    discovery_visibility: prefs?.discovery_visibility || (prefs?.is_available_for_trustbud ? "visible" : "hidden"),
    has_seen_discovery_prompt: Boolean(prefs?.has_seen_discovery_prompt),
    show_profile_image: Boolean(prefs?.show_profile_image),
    show_profile_headline: Boolean(prefs?.show_profile_headline),
    show_profile_focus: Boolean(prefs?.show_profile_focus),
    show_discovery_headline: Boolean(prefs?.show_discovery_headline ?? prefs?.show_profile_headline),
    show_discovery_focus_area: Boolean(prefs?.show_discovery_focus_area ?? prefs?.show_profile_focus),
    show_discovery_activity: Boolean(prefs?.show_discovery_activity),
    share_goal_summary_with_connections: Boolean(prefs?.share_goal_summary_with_connections),
    share_focus_statement_with_connections: Boolean(prefs?.share_focus_statement_with_connections),
    share_sprint_goal: Boolean(prefs?.share_sprint_goal ?? true),
    share_sprint_actions: Boolean(prefs?.share_sprint_actions ?? true),
    share_sprint_progress: Boolean(prefs?.share_sprint_progress),
    show_on_network_leaderboards: Boolean(prefs?.show_on_network_leaderboards ?? true),
    leaderboard_share_level: prefs?.leaderboard_share_level || "activity_only",
    allow_support_recognition: Boolean(prefs?.allow_support_recognition ?? true),
    show_consistency_label: Boolean(prefs?.show_consistency_label),
    show_reliability_label: Boolean(prefs?.show_reliability_label),
    show_trustbud_score_label: Boolean(prefs?.show_trustbud_score_label),
    show_trustbud_percentile: Boolean(prefs?.show_trustbud_percentile),
    show_renewal_rate: Boolean(prefs?.show_renewal_rate),
    show_completed_windows: Boolean(prefs?.show_completed_windows),
  };
}

const defaultCommunicationPreferences = {
  coaching_reminders: true,
  planned_time_alerts: true,
  celebrations: true,
  sprint_accountability: true,
  network_activity: true,
  weekly_summaries: true,
};

const communicationPreferenceRows = [
  ["coaching_reminders", "Coaching reminders", "Start, continue, and rescue messages during the day."],
  ["planned_time_alerts", "Planned time alerts", "A note when a planned time passes and the habit is still open."],
  ["celebrations", "Celebrations", "Clean sweeps, reset days, and recovery wins."],
  ["sprint_accountability", "Accountability", "Daily Report reminders and accountability moments."],
  ["network_activity", "People activity", "Occasional reminders to check in with your people."],
  ["weekly_summaries", "Weekly summaries", "A note when your weekly recap is ready."],
];

function normalizeCommunicationPreferences(prefs = {}) {
  return {
    ...defaultCommunicationPreferences,
    ...(prefs?.communication_preferences || {}),
  };
}

function AccountSettingsPage() {
  const navigate = useNavigate();
  const { isPlus: subscriptionActive } = useEntitlements();
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [profileHeadline, setProfileHeadline] = useState("");
  const [profileFocus, setProfileFocus] = useState("");
  const [activeTrustBudsCount, setActiveTrustBudsCount] = useState(0);
  const [pastTrustBudsCount, setPastTrustBudsCount] = useState(0);
  const [photoStatus, setPhotoStatus] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusColor, setStatusColor] = useState("text.secondary");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [preferences, setPreferences] = useState({
    is_available_for_trustbud: false,
    discovery_visibility: "hidden",
    has_seen_discovery_prompt: false,
    show_profile_image: false,
    show_profile_headline: false,
    show_profile_focus: false,
    show_discovery_headline: false,
    show_discovery_focus_area: false,
    show_discovery_activity: false,
    share_goal_summary_with_connections: false,
    share_focus_statement_with_connections: false,
    share_sprint_goal: true,
    share_sprint_actions: true,
    share_sprint_progress: false,
    show_on_network_leaderboards: true,
    leaderboard_share_level: "activity_only",
    allow_support_recognition: true,
    show_consistency_label: false,
    show_reliability_label: false,
    show_trustbud_score_label: false,
    show_trustbud_percentile: false,
    show_renewal_rate: false,
    show_completed_windows: false,
  });
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [supportPrivacyPreferences, setSupportPrivacyPreferences] = useState({});
  const [communicationPreferences, setCommunicationPreferences] = useState(defaultCommunicationPreferences);
  const [communicationPreferencesSaving, setCommunicationPreferencesSaving] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    let active = true;
    async function loadSettings() {
      setLoading(true);
      try {
        const data = await fetchAccountSettings();
        const prefs = await fetchTrustBudPreferences();
        const supportProfile = await fetchSupportProfile();
        if (active) {
          setEmail(data?.email || "");
          setUsername(data?.username || "");
          setProfileImage(data?.profile_image || "");
          setProfileHeadline(data?.profile_headline || "");
          setProfileFocus(data?.profile_focus || "");
          setActiveTrustBudsCount(Number(data?.active_trustbuds_count ?? 0));
          setPastTrustBudsCount(Number(data?.past_trustbuds_count ?? 0));
          setPreferences(normalizeTrustBudPreferences(prefs));
          setSupportPrivacyPreferences(supportProfile?.privacy_preferences || {});
          setCommunicationPreferences(normalizeCommunicationPreferences(supportProfile?.privacy_preferences || {}));
        }
      } catch (error) {
        if (active) {
          setStatusMessage("Unable to load account settings.");
          setStatusColor("error.main");
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    loadSettings();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!statusMessage) return;
    const timer = setTimeout(() => setStatusMessage(""), 3000);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  async function handleChangePassword() {
    setStatusMessage("");
    if (!currentPassword || !newPassword) {
      setStatusMessage("Enter your current and new password.");
      setStatusColor("error.main");
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatusMessage("New passwords do not match.");
      setStatusColor("error.main");
      return;
    }
    try {
      await changeAccountPassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setStatusMessage("Password updated.");
      setStatusColor("success.main");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      setStatusMessage(error.message || "Unable to update password.");
      setStatusColor("error.main");
    }
  }

  async function handleDeleteAccount() {
    if (deletingAccount) return;
    const confirmed = window.confirm(
      "Delete your account? This cannot be undone."
    );
    if (!confirmed) return;
    setDeletingAccount(true);
    setStatusMessage("Deleting your account...");
    setStatusColor("text.secondary");
    try {
      await deleteAccount();
      logout();
      navigate("/login", { replace: true });
    } catch (error) {
      setStatusMessage(error.message || "Unable to delete account.");
      setStatusColor("error.main");
      setDeletingAccount(false);
    }
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  async function handlePhotoChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotoStatus("Uploading...");
    try {
      const formData = new FormData();
      formData.append("profile_image", file);
      const response = await updateProfileImage(formData);
      setProfileImage(response?.profile_image || "");
      setPhotoStatus("Photo updated.");
    } catch (error) {
      setPhotoStatus(error.message || "Unable to upload photo.");
    }
  }

  async function handleSaveCommunicationPreferences() {
    setCommunicationPreferencesSaving(true);
    setStatusMessage("");
    try {
      const privacy_preferences = {
        ...supportPrivacyPreferences,
        communication_preferences: communicationPreferences,
      };
      const updated = await updateSupportProfile({ privacy_preferences });
      const nextPrivacy = updated?.privacy_preferences || privacy_preferences;
      setSupportPrivacyPreferences(nextPrivacy);
      setCommunicationPreferences(normalizeCommunicationPreferences(nextPrivacy));
      setStatusMessage("Communication settings saved.");
      setStatusColor("success.main");
    } catch (error) {
      setStatusMessage(error.message || "Unable to save communication settings.");
      setStatusColor("error.main");
    } finally {
      setCommunicationPreferencesSaving(false);
    }
  }

  async function handleRemovePhoto() {
    setPhotoStatus("Removing...");
    try {
      const formData = new FormData();
      formData.append("remove_profile_image", "true");
      const response = await updateProfileImage(formData);
      setProfileImage(response?.profile_image || "");
      setPhotoStatus("Photo removed.");
    } catch (error) {
      setPhotoStatus(error.message || "Unable to remove photo.");
    }
  }

  return (
    <Box sx={{ maxWidth: 560, mx: "auto", px: { xs: 1.5, sm: 0 } }}>
      {statusMessage ? (
        <Alert
          severity={
            statusColor === "success.main"
              ? "success"
              : statusColor === "error.main"
                ? "error"
                : "info"
          }
          sx={{ mb: 2 }}
        >
          {statusMessage}
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Account Settings
        </Typography>
        {subscriptionActive && (
          <Chip label="TrustBud Plus" color="primary" size="small" />
        )}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
          mb: 2,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Profile photo
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              component="img"
              src={profileImage || ""}
              alt=""
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "grey.100",
                border: "1px solid",
                borderColor: "divider",
                display: profileImage ? "block" : "none",
                objectFit: "cover",
              }}
            />
            {!profileImage && (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "grey.100",
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                }}
              >
                {(username || email || "TB").slice(0, 2).toUpperCase()}
              </Box>
            )}
            <Stack spacing={1}>
              <Button variant="outlined" component="label">
                Upload photo
                <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
              </Button>
              {profileImage && (
                <Button color="error" variant="text" onClick={handleRemovePhoto}>
                  Remove photo
                </Button>
              )}
            </Stack>
          </Stack>
          {photoStatus && (
            <Typography variant="caption" color="text.secondary">
              {photoStatus}
            </Typography>
          )}

          <Stack spacing={1.5}>
            <TextField
              label="Headline"
              value={profileHeadline}
              onChange={(e) => setProfileHeadline(e.target.value)}
              placeholder="Building consistent habits"
              fullWidth
              helperText="Optional"
            />
            <TextField
              label="Focus area"
              value={profileFocus}
              onChange={(e) => setProfileFocus(e.target.value)}
              placeholder="What are you focusing on right now?"
              fullWidth
              helperText="Optional"
            />
            <Button
              variant="contained"
              onClick={async () => {
                setPhotoStatus("");
                setStatusMessage("");
                try {
                  const formData = new FormData();
                  formData.append("profile_headline", profileHeadline);
                  formData.append("profile_focus", profileFocus);
                  const response = await updateProfileImage(formData);
                  setProfileHeadline(response?.profile_headline || "");
                  setProfileFocus(response?.profile_focus || "");
                  setActiveTrustBudsCount(
                    Number(response?.active_trustbuds_count ?? activeTrustBudsCount)
                  );
                  setPastTrustBudsCount(
                    Number(response?.past_trustbuds_count ?? pastTrustBudsCount)
                  );
                  setStatusMessage("Profile text saved.");
                  setStatusColor("success.main");
                } catch (error) {
                  const message =
                    error.message || "Unable to save profile text.";
                  setPhotoStatus(message);
                  setStatusMessage(message);
                  setStatusColor("error.main");
                }
              }}
            >
              Save profile text
            </Button>
          </Stack>

          <Stack direction="row" spacing={4} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Active TrustBuds
              </Typography>
              <Typography variant="body2">
                {activeTrustBudsCount}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Past TrustBuds
              </Typography>
              <Typography variant="body2">
                {pastTrustBudsCount}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
          mb: 2,
        }}
      >
        <TextField
          label="Email"
          value={email}
          fullWidth
          InputProps={{ readOnly: true }}
          disabled={loading}
        />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
          mb: 2,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Coach communications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose what TrustBud can send as a push. In-app coaching moments still appear when they are useful.
          </Typography>
          {communicationPreferenceRows.map(([key, title, body]) => (
            <Stack
              key={key}
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>{title}</Typography>
                <Typography variant="caption" color="text.secondary">{body}</Typography>
              </Box>
              <Switch
                checked={Boolean(communicationPreferences[key])}
                onChange={(event) =>
                  setCommunicationPreferences((prev) => ({
                    ...prev,
                    [key]: event.target.checked,
                  }))
                }
              />
            </Stack>
          ))}
          <Button
            variant="outlined"
            disabled={communicationPreferencesSaving}
            onClick={handleSaveCommunicationPreferences}
          >
            {communicationPreferencesSaving ? "Saving..." : "Save communication settings"}
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
          mb: 2,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Privacy & Discovery
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose whether people can find you in Connections. Accountability privacy is controlled separately below.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sensitive goals use private notification copy and private proof details by default.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2">Searchable in Connections</Typography>
              <Typography variant="body2" color="text.secondary">
                {preferences.discovery_visibility === "visible"
                  ? "People can find you and send a TrustBud request."
                  : "People cannot find you through search."}
              </Typography>
            </Box>
            <Switch
              checked={preferences.discovery_visibility === "visible"}
              onChange={(event) =>
                setPreferences((prev) => ({
                  ...prev,
                  discovery_visibility: event.target.checked ? "visible" : "hidden",
                  is_available_for_trustbud: event.target.checked,
                  has_seen_discovery_prompt: true,
                  show_discovery_headline: event.target.checked,
                  show_discovery_focus_area: event.target.checked,
                  show_discovery_activity: event.target.checked,
                }))
              }
            />
          </Stack>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Show goal summary to connections</Typography>
              <Switch
                checked={preferences.share_goal_summary_with_connections}
                onChange={(event) =>
                  setPreferences((prev) => ({
                    ...prev,
                    share_goal_summary_with_connections: event.target.checked,
                  }))
                }
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Show focus statement to connections</Typography>
              <Switch
                checked={preferences.share_focus_statement_with_connections}
                onChange={(event) =>
                  setPreferences((prev) => ({
                    ...prev,
                    share_focus_statement_with_connections: event.target.checked,
                  }))
                }
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Show Accountability goal to your TrustBud</Typography>
              <Switch
                checked={preferences.share_sprint_goal}
                onChange={(event) =>
                  setPreferences((prev) => ({
                    ...prev,
                    share_sprint_goal: event.target.checked,
                  }))
                }
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Show Accountability habits to your TrustBud</Typography>
              <Switch
                checked={preferences.share_sprint_actions}
                onChange={(event) =>
                  setPreferences((prev) => ({
                    ...prev,
                    share_sprint_actions: event.target.checked,
                  }))
                }
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Show Accountability progress to your TrustBud</Typography>
              <Switch
                checked={preferences.share_sprint_progress}
                onChange={(event) =>
                  setPreferences((prev) => ({
                    ...prev,
                    share_sprint_progress: event.target.checked,
                  }))
                }
              />
            </Stack>
          </Stack>
          <Stack spacing={1.3} sx={{ pt: 1 }}>
            <Typography variant="subtitle2">Activity leaderboard privacy</Typography>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Show me on leaderboards</Typography>
              <Switch
                checked={preferences.show_on_network_leaderboards}
                onChange={(event) =>
                  setPreferences((prev) => ({
                    ...prev,
                    show_on_network_leaderboards: event.target.checked,
                    leaderboard_share_level: event.target.checked ? prev.leaderboard_share_level : "private",
                  }))
                }
              />
            </Stack>
            <TextField
              select
              label="Leaderboard sharing"
              value={preferences.leaderboard_share_level}
              onChange={(event) =>
                setPreferences((prev) => ({
                  ...prev,
                  leaderboard_share_level: event.target.value,
                  show_on_network_leaderboards: event.target.value !== "private",
                }))
              }
              fullWidth
              helperText="Activity only keeps habit names out of public leaderboard rows."
            >
              <MenuItem value="activity_only">Activity only</MenuItem>
              <MenuItem value="habit_names">Habit names</MenuItem>
              <MenuItem value="private">Private</MenuItem>
            </TextField>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Allow support recognition</Typography>
              <Switch
                checked={preferences.allow_support_recognition}
                onChange={(event) =>
                  setPreferences((prev) => ({
                    ...prev,
                    allow_support_recognition: event.target.checked,
                  }))
                }
              />
            </Stack>
          </Stack>
          <Button
            variant="outlined"
            disabled={preferencesSaving}
            onClick={async () => {
              setPreferencesSaving(true);
              setStatusMessage("");
              try {
                const updated = await updateTrustBudPreferences(preferences);
                setPreferences(normalizeTrustBudPreferences(updated));
                setStatusMessage("Privacy settings saved.");
                setStatusColor("success.main");
              } catch (error) {
                setStatusMessage(error.message || "Unable to save privacy settings.");
                setStatusColor("error.main");
              } finally {
                setPreferencesSaving(false);
              }
            }}
          >
            {preferencesSaving ? "Saving..." : "Save privacy settings"}
          </Button>
          <Button variant="text" onClick={() => navigate("/trustbud-plus")}>
            What Plus Unlocks
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
          mb: 2,
        }}
      >
        <Stack spacing={2}>
          <TextField
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            fullWidth
          />
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            fullWidth
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            fullWidth
          />
          <Button variant="contained" onClick={handleChangePassword}>
            Change password
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2.5,
        }}
      >
        <Stack spacing={2}>
          <Button variant="outlined" onClick={handleLogout}>
            Log out
          </Button>
          <Button color="error" onClick={handleDeleteAccount} disabled={deletingAccount}>
            {deletingAccount ? "Deleting account..." : "Delete account"}
          </Button>
        </Stack>
      </Paper>

      {statusMessage && (
        <Typography variant="body2" sx={{ mt: 2, color: statusColor }}>
          {statusMessage}
        </Typography>
      )}
    </Box>
  );
}

export default AccountSettingsPage;
